import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Card, CardContent, Grid, Divider, TextField, CardActions, Tooltip, IconButton, Button, Typography, Stack } from "@mui/material";
import Chart from 'react-apexcharts';
import { useData } from "../../../context/DataContext";
import SingleSelectComponent from "../../../components/Selections/SingleSelectComponent";
import MaterialReactTableWithSearch from "../../../components/MaterialReactTable/MaterialReactTableWithSearch";
import LoadingComponent from "../../../components/Loading/LoadingComponent";
import MultiSelectComponent from "../../../components/Selections/MultiSelectComponent";
import SingleSelect from "../../../components/Selections/singleSelect";
import CustomButton from "../../../components/buttons/customButton";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation } from "react-router-dom";
import MultiSelectWithAll from "../../../components/Selections/multiSelectWithAll";


const SoilSummaryReport = () => {
    const { user = {}, soilReportSummary = [], years = [], handleSoilReportSummary, subLoading, villageGroups = [] } = useData();
    const navigate = useNavigate();
    const ffGroupData = villageGroups.filter((item) => item.level_id === 4);
    const scrollRef = useRef(null);

    const groupData = ffGroupData[0]?.data?.filter(item => item.group_id !== "").map((item) => ({ value: item.group_id, label: item.Groups }));
    const villageListData = ffGroupData[0]?.data?.filter((item) => item.village_id !== "").map((item) => ({ value: +item.village_id, label: item.village }));


    const [state, setState] = useState({
        selected_group_ids: [],
        selected_village_ids: [],
        selected_group_ids_error: "",
        villageDetails: [],
        groupDetails: []
    });

    const [yearId, setYearId] = useState(null);
    const [selectedChatType, setSelectedChatType] = useState(1);
    const [selectedBtnType, setSelectedBtnType] = useState(11);
    const [tableData, setTableData] = useState([]);

    const totalVillages = useMemo(() => soilReportSummary?.[0]?.data?.length || 0, [soilReportSummary]);
    const villageReportCompleted = useMemo(() => soilReportSummary?.[0]?.data?.filter((item) => item.village_is_mapped === 1).length || 0, [soilReportSummary]);
    const villageReportPending = totalVillages - villageReportCompleted;

    const totalVillageFarmers = useMemo(() => soilReportSummary?.[1]?.data?.reduce((acc, curr) => acc + curr.no_of_farmers, 0) || 0, [soilReportSummary]);
    const completedVillageFarmersReport = useMemo(() => soilReportSummary?.[1]?.data?.reduce((acc, curr) => acc + curr.farmer_mapped, 0) || 0, [soilReportSummary]);
    const pendingVillageFarmersReport = totalVillageFarmers - completedVillageFarmersReport;


    const totalGroups = useMemo(() => soilReportSummary?.[2]?.data?.length || 0, [soilReportSummary]);
    const totalGroupFarmers = useMemo(() => soilReportSummary?.[2]?.data?.reduce((acc, curr) => acc + curr.no_of_farmers, 0) || 0, [soilReportSummary]);
    const completedGroupFarmersReport = useMemo(() => soilReportSummary?.[2]?.data?.reduce((acc, curr) => acc + curr.farmer_mapped, 0) || 0, [soilReportSummary]);
    const pendingGroupFarmersReport = totalGroupFarmers - completedGroupFarmersReport;

    const generateChartData = (series, labels) => {
        if (soilReportSummary.length > 0) {

            const allZero = series.every(value => value === 0);
            console.log("seriessss", allZero, series);


            if (allZero === true) {
                return {
                    series: [1],
                    options: {
                        chart: {
                            type: 'donut',
                        },
                        labels: ['No Data'],
                        colors: ['#989898'],
                        tooltip: {
                            enabled: false,
                        },
                        fill: {
                            type: 'gradient',
                        },
                    },
                };
            } else if (allZero === false) {
                return {
                    series,
                    options: {
                        chart: {
                            type: 'donut',
                        },
                        labels,
                        fill: {
                            type: 'gradient',
                        },
                    },
                };
            }

        }


    };

    const villageData = generateChartData([0, villageReportCompleted, villageReportPending], [`Total Villages (${totalVillages})`, `Mapped (${villageReportCompleted})`, `Yet to Mapped (${villageReportPending})`]);
    const villageFarmersData = generateChartData([0, 0, completedVillageFarmersReport, pendingVillageFarmersReport], [`Total Villages (${totalVillages})`, `Total Farmers (${totalVillageFarmers})`, `Mapped (${completedVillageFarmersReport})`, `Yet to Mapped (${pendingVillageFarmersReport})`]);
    const groupFarmersData = generateChartData([0, 0, completedGroupFarmersReport, pendingGroupFarmersReport], [`Total Groups (${totalGroups})`, `Total Farmers (${totalGroupFarmers})`, `Mapped (${completedGroupFarmersReport})`, `Yet to Mapped (${pendingGroupFarmersReport})`]);

    const soilSummaryColumns = useMemo(() => {
        let additionalColumns = [];

        if (selectedChatType === 1) {
            additionalColumns = [
                { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id' },
                { accessorKey: "village_name", header: "Village Name" }
            ];
        } else if (selectedChatType === 2) {
            additionalColumns = [
                { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id' },
                { accessorKey: "village_name", header: "Village Name" },
                { accessorKey: "no_of_farmers", header: "Total Farmers" },
                { accessorKey: "farmer_mapped", header: "Mapped" },
                {
                    accessorFn: (row) => {
                        const noOfFarmers = row.no_of_farmers || 0; // Default to 0 if undefined
                        const farmerMapped = row.farmer_mapped || 0; // Default to 0 if undefined
                        return noOfFarmers - farmerMapped; // Calculate pending count
                    },
                    header: "Yet to Mapped"
                },];
        } else if (selectedChatType === 3) {
            additionalColumns = [
                { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id' },
                { accessorKey: "group_name", header: "Group Name" },
                { accessorKey: "no_of_farmers", header: "Total Farmers" },
                { accessorKey: "farmer_mapped", header: "Mapped" },
                {
                    accessorFn: (row) => {
                        const noOfFarmers = row.no_of_farmers || 0; // Default to 0 if undefined
                        const farmerMapped = row.farmer_mapped || 0; // Default to 0 if undefined
                        return noOfFarmers - farmerMapped; // Calculate pending count
                    },
                    header: "Yet to Mapped"
                },];
        }

        return additionalColumns;
    }, [selectedChatType]);


    const handleChatClick = (type) => {
        setSelectedChatType(type);
        if (type === 1) {
            setTableData(soilReportSummary[0].data?.filter((item) => item.village_is_mapped === 1) || []);
            setState(prevState => ({
                ...prevState,
                selected_village_ids: [],
                selected_group_ids: []
            }));
        } else if (type === 2) {
            setTableData(soilReportSummary?.[1]?.data)
            setState(prevState => ({
                ...prevState,
                villageDetails: villageListData || [],
                selected_village_ids: villageListData || [],
                selected_group_ids: []
            }));
        } else if (type === 3) {
            setState(prevState => ({
                ...prevState,
                groupDetails: groupData || [],
                selected_group_ids: groupData || [],
                selected_village_ids: []
            }));
            setTableData(soilReportSummary?.[2]?.data)
        }
    };


    const handleTypeBtnClick = (type) => {
        setSelectedBtnType(type);
        if (type === 11) {
            setTableData(soilReportSummary?.[0]?.data?.filter((item) => item.village_is_mapped === 1))
        } else if (type === 12) {
            setTableData(soilReportSummary?.[0]?.data?.filter((item) => item.village_is_mapped === 0))
        }
    };

    const handleInputChange = (name, value) => {
        if (name === 'yearId') {
            setYearId(value);
            handleSoilReportSummary(value);
        }
    };

    useEffect(() => {
        setYearId(years[0]?.value);
        handleSoilReportSummary(years[0]?.value);
        if (user.level_id === 2) {
            setSelectedChatType(3)
            setState(prevState => ({
                ...prevState,
                groupDetails: groupData || [],
                selected_group_ids: groupData || [],
                selected_village_ids: []
            }));
        } else {
            setSelectedChatType(1)
        }
    }, [years, user.level_id]);



    useEffect(() => {
        if (soilReportSummary.length > 0) {
            if (user.level_id === 2) {
                setTableData(soilReportSummary?.[2]?.data)

            } else {
                setTableData(soilReportSummary[0].data?.filter((item) => item.village_is_mapped === 1) || []);
            }
        }
    }, [soilReportSummary]);

    const handleMultiselectVillagechange = (name, selectedOption = []) => {
        if (selectedOption.length > 0) {
            const selectedVillageIds = selectedOption.map(option => option.value);

            const filteredTableData = soilReportSummary[1]?.data?.filter(item =>
                selectedVillageIds.includes(item.village_id.toString())
            ) || [];

            setState(prevState => ({
                ...prevState,
                selected_village_ids: selectedOption,
                selected_group_ids_error: "",
            }));
            setTableData(filteredTableData)
        } else {
            setState(prevState => ({
                ...prevState,
                selected_village_ids: [],
                selected_group_ids_error: "",
            }));
            setTableData([])
        }
    };


    const handleMultiselectGroupchange = (name, options = []) => {
        if (options.length > 0) {
            const selectedGroupIds = options.map(option => option.value);

            const filteredTableData = soilReportSummary[2]?.data?.filter(item =>
                selectedGroupIds.includes(item.ff_group_id.toString())
            ) || [];

            setState(prevState => ({
                ...prevState,
                selected_group_ids: options,
                selected_group_ids_error: "",
            }));
            setTableData(filteredTableData)
        } else {
            setState(prevState => ({
                ...prevState,
                selected_group_ids: [],
                selected_group_ids_error: "",
            }));
            setTableData([])
        }
    };



    if (subLoading) {
        return <LoadingComponent />;
    }


    const renderChartColumn = (type, chartData, title) => {
        if (soilReportSummary.length > 0) {
            return (
                <Grid item size={{ xs: 12, md: 4 }}
                    onClick={() => handleChatClick(type)}
                    sx={{
                        boxShadow: 3,
                        cursor: "pointer",
                        border: selectedChatType === type ? "2px solid #1D5E40" : "2px solid transparent",
                        borderRadius: 1,
                        textAlign: "center",
                        '&:hover': {
                            border: "2px solid #1D5E40"
                        }
                    }}
                >
                    <Chart options={chartData.options} series={chartData.series} type="donut" height={170} />
                    <Box sx={{ textAlign: "center", mt: 2, mb: 2 }}>
                        <Typography color="success" variant="bold">{title}</Typography>
                    </Box>
                </Grid>
            );
        }

    };

    const sessionButtons =
        selectedChatType === 1
            ? [
                {
                    label: "Completed",
                    value: 11,
                    count: villageReportCompleted,
                },
                {
                    label: "Pending",
                    value: 12,
                    count: villageReportPending,
                },
            ]
            : [];

 
    return (
        <Box>
            <Divider sx={{  bgcolor: '#f5f1f1ff' }} />

            <Card elevation={6} sx={{ mt: 1 }}>
                <Grid container alignItems="center" mt={1} mx={1}>
                    <Grid item size={{ xs: 12, md: 8 }} textAlign="start">
                        <Typography variant="h5" textTransform="uppercase">
                            Soil Report
                        </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 4 }} display="flex" justifyContent="flex-end">
                        <SingleSelect
                            name="yearId"
                            label="Select Year"
                            options={years}
                            value={yearId}
                            onChange={handleInputChange}
                        />
                    </Grid>
                </Grid>
                <Divider sx={{ bgcolor: '#f5f1f1ff', my: 1 }} />

                <CardContent>
                    <Grid container spacing={1}>
                        {user.level_id === 1 || user.level_id === null ? (
                            <>
                                {renderChartColumn(1, villageData, "Villages")}
                                {renderChartColumn(2, villageFarmersData, "Village Farmers")}
                                {user.level_id === null && renderChartColumn(3, groupFarmersData, "Group Farmers")}
                            </>
                        ) : user.level_id === 2 ? (
                            renderChartColumn(3, groupFarmersData, "Group Farmers")
                        ) : null}
                    </Grid>

                    <Grid container spacing={1}>
                        {selectedChatType === 1 ? (
                            <Box
                                ref={scrollRef}
                                sx={{
                                    overflowX: "auto",
                                    whiteSpace: "nowrap",
                                    scrollBehavior: "smooth",
                                    width: "100%",
                                    px: 1,
                                    mb: 2,
                                    "&::-webkit-scrollbar": { display: "none" },
                                }}
                            >
                                <Stack direction="row" spacing={1} mt={2}>
                                    {sessionButtons.map((btn) => (
                                        <Button
                                            key={btn.value}
                                            variant={selectedBtnType === btn.value ? "contained" : "outlined"}
                                            color="success"
                                            onClick={() => handleTypeBtnClick(btn.value)}
                                            sx={{
                                                textTransform: "none", borderRadius: 2,
                                                "&:hover": {
                                                    backgroundColor: selectedBtnType === btn.value
                                                        ? "success.dark"
                                                        : "success.main",
                                                    color: "#fff",
                                                    borderColor: "success.main",
                                                },
                                            }}
                                        >
                                            {btn.label} ({btn.count})
                                        </Button>
                                    ))}
                                </Stack>
                            </Box>
                        ) : selectedChatType === 2 ? (
                            <Grid item size={{ xs: 12, md: 4 }} mt={2} mb={2}>
                                <MultiSelectWithAll
                                    name="selected_village_ids"
                                    label="Select Villages"
                                    options={state.villageDetails}
                                    value={state.selected_village_ids || []}
                                    onChange={handleMultiselectVillagechange}
                                />
                            </Grid>
                        ) : (
                            <Grid item size={{ xs: 12, md: 4 }} mt={2} mb={2}>
                                <MultiSelectWithAll
                                    name="selected_group_ids"
                                    label="Select Groups"
                                    options={state.groupDetails}
                                    value={state.selected_group_ids || []}
                                    onChange={handleMultiselectGroupchange}
                                />
                            </Grid>
                        )
                        }
                    </Grid>

                    <Grid container spacing={1}>
                        <Grid item size={{ xs: 12, md: 12 }} sx={{ boxShadow: 6 }}>
                            <MaterialReactTableWithSearch data={tableData} columns={soilSummaryColumns} />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SoilSummaryReport;
